import { deserialize, DocumentObject, serialize } from '@jsonapiworld/jsonapi-serializer';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { fetchCustomerPortalUrl, fetchNewSecondCompanySubscriptionInfo } from '~/api/subscription';
import { backendApiGateway } from '~/client/backend';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import { MetaDataCursorPage, pagedMetaSchema } from '~/types/jsonapi';
import { Subscription, SubscriptionQueryKeys, SubscriptionsMeta } from '~/types/subscriptions';
import { buildJsonAPIQueryParams, jsonAPICursorPaginatorBuilder, jsonAPIFilterBuilder } from '~/utils/jsonapi-query';
import { addItemToQueriesCache } from '~/utils/queryCache';
import { getCurrentSubscription } from '~/utils/subscriptions';

const SUBSCRIPTION_DEFAULT_PAGINATION_SIZE = 25;

const fetchSubscriptions = async (companyId: string) => {
  let allSubscriptions: Subscription[] = [];
  let fetchedSubscriptions: Subscription[] = [];

  let cursor: MetaDataCursorPage['cursor'] | null = null;

  const filterBuider = jsonAPIFilterBuilder();
  filterBuider.eq('company.id', companyId);
  do {
    const pageBuilder = jsonAPICursorPaginatorBuilder({ size: SUBSCRIPTION_DEFAULT_PAGINATION_SIZE });
    if (cursor) pageBuilder.cursor(cursor);

    const searchParams = buildJsonAPIQueryParams(filterBuider, pageBuilder);
    const { data: results } = await backendApiGateway.get<DocumentObject & { meta: SubscriptionsMeta }>(
      '/api/v1/subscriptions',
      { params: searchParams },
    );
    fetchedSubscriptions = deserialize<Subscription>(results) as Subscription[];
    allSubscriptions = allSubscriptions.concat(fetchedSubscriptions);
    const meta = pagedMetaSchema.parse(results.meta);
    cursor = meta.page.cursor;
  } while (fetchedSubscriptions.length === SUBSCRIPTION_DEFAULT_PAGINATION_SIZE);
  return allSubscriptions;
};

export const useNavigateToCustomerPortal = () => {
  const { data: subscription } = useCurrentSubscription();

  return useCallback(
    async ({ returnUrl }: { returnUrl?: string } = {}) => {
      if (!subscription) {
        return;
      }
      const result = await fetchCustomerPortalUrl(subscription.id, returnUrl ?? window.location.href);
      window.location.href = result.url;
    },
    [subscription],
  );
};

export const useNavigateToSecondCompanyCustomerPortal = () => {
  return useCallback(async ({ returnUrl, subscriptionId }: { returnUrl: string; subscriptionId: string }) => {
    const result = await fetchCustomerPortalUrl(subscriptionId, returnUrl);
    window.location.href = `${result.url}/payment-methods`;
  }, []);
};

const createSubscription = async (companyId: string) => {
  const payload = serialize({ company: { id: companyId } }, 'subscription', {
    relationships: ['company'],
  });
  const { data } = await backendApiGateway.post<DocumentObject>('api/v1/subscriptions', payload);

  return deserialize<Subscription>(data) as Subscription;
};

export const useCreateSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (companyId: string) => createSubscription(companyId),
    onSuccess: (data, companyId) => {
      addItemToQueriesCache<Subscription>(queryClient, SubscriptionQueryKeys.Subscriptions(companyId), data);
      queryClient.invalidateQueries({ queryKey: SubscriptionQueryKeys.Subscriptions(companyId) });
    },
  });
};

const useSubscriptions = () => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: SubscriptionQueryKeys.Subscriptions(companyId),
    queryFn: () => fetchSubscriptions(companyId || ''),
    enabled: Boolean(companyId),
    staleTime: Infinity,
  });
};

export const useCurrentSubscription = () => {
  const { data: subscriptions, ...rest } = useSubscriptions();
  return { data: useMemo(() => getCurrentSubscription(subscriptions), [subscriptions]), ...rest };
};

export const useFetchNewSecondCompanySubscriptionInfo = () =>
  useMutation({
    mutationFn: () => fetchNewSecondCompanySubscriptionInfo(),
  });
